/**
 * Methods for form interaction.
 *
 * Used in conjunction with styles/components/_form.scss
 *
 * Implementation example:
 * ```
 * EN.Forms.init();
 * EN.Forms.initAjax();
 * EN.Forms.initInputStates();
 * ```
 *
 * @module Forms
 * @author EN GARDE (Martin Holler)
 * @version 1
 */

var EN = EN || {};

EN.Forms = (function (_, $) {
	'use strict';

	var options = {
		inputSelector: '[name]:not([type="hidden"])',
		labelSelector: '.en-label',
		conditionalAttribute: 'data-en-form-conditional-rules',
		classNames: {
			hidden: 'en-hidden',
			success: 'en-form--success',
			error: 'en-form--error',

			// elements
			form: 'en-form',
			formAjax: 'en-form--ajax',
			status: 'en-form__status',
			statusText: 'en-form__status-text',

			// state classes
			loading: 'en-is-loading',
			valid: 'en-is-valid',
			invalid: 'en-is-invalid',
			pristine: 'en-is-pristine',
			dirty: 'en-is-dirty',
			empty: 'en-is-empty',
			filled: 'en-is-filled',
			required: 'en-is-required'
		}
	};

	/**
	 * Returns an input's value and checks for its type. If it is a toggle (i.e. checkbox or radio) it will only return the value if the input is checked.
	 * @param {jQuery} $input
	 * @return {string}
	 */
	var _getInputValue = function ($input) {
		if (!$input.length) {
			return '';
		}

		var value = $input.val();
		var isToggle;

		if (value) {
			isToggle = $input.attr('type') === 'radio' || $input.attr('type') === 'checkbox';

			// reset the value for non-checked toggles
			if (isToggle && !$input.is(':checked')) {
				value = '';
			}
		}

		return value;
	};

	/**
	 * Sets valid/invalid class names to all input elements in a given form.
	 * @param {jQuery} $form
	 * @param {string[]} errorFields - Array of invalid field names.
	 * @return {jQuery}
	 * @memberof module:Forms
	 */
	var setFieldClasses = function ($form, errorFields) {
		$form = $form.length ? $form : $(document.body);
		errorFields = $.isArray(errorFields) ? errorFields : [];

		var $fields = $form.find(options.inputSelector);

		$fields.addClass(options.classNames.valid);
		$fields.removeClass(options.classNames.invalid);

		if (errorFields.length) {
			var selector = '[name="' + errorFields.join('"], [name="') + '"], [name="' + errorFields.join('[]"], [name="') + '[]"]';
			var $targetFields = $fields.filter(selector);

			$targetFields.removeClass(options.classNames.valid);
			$targetFields.addClass(options.classNames.invalid);
		}

		return $fields;
	};

	/**
	 * Initializes Ajax-based form submission. Also sets field classes to input elements.
	 * @param {object} [settings] - Overrides defaults, see below.
	 * @param {function} settings.onsuccess - Function to be called when the submission succeeds.
	 * @param {function} settings.onerror - Function to be called when the submission fails.
	 * @memberof module:Forms
	 */
	var initAjax = function (settings) {
		settings = settings || {};

		$(document).on('submit', '.' + options.classNames.formAjax, function (e) {
			var $form = $(this);
			var $status = $form.find('.' + options.classNames.status);
			var $statusText = $form.find('.' + options.classNames.statusText);

			if (!$statusText.length) {
				$statusText = $status;
			}

			// bail if loading
			if ($form.hasClass(options.classNames.loading)) {
				e.preventDefault();
				return;
			}

			$form.addClass(options.classNames.loading);

			$.ajax({
				method: 'POST',
				url: $form.attr('action'),
				data: $form.serialize(),
				success: function (data) {

					// reset classes
					$form.removeClass(options.classNames.loading);
					$form.removeClass(options.classNames.error);
					$form.removeClass(options.classNames.success);
					$status.addClass(options.classNames.hidden);

					if (data.message) {
						$statusText.html(data.message);
						$status.removeClass(options.classNames.hidden);
					}

					// form success
					if (data.success) {
						$form.addClass(options.classNames.success);


						if (typeof settings.onsuccess === 'function') {
							settings.onsuccess($form, data);
						}

						$form.trigger('formsuccess');

						// redirect
						if (data.redirect) {
							window.location.href = data.redirect;
						}
					}
					// form error
					else {
						$form.addClass(options.classNames.error);

						if (typeof settings.onerror === 'function') {
							settings.onerror($form, data);
						}

						$form.trigger('formerror');
					}

					setFieldClasses($form, data.errorFields);
				},
				error: function () {
					console.log('[Form.js] Ajax request failed. Submitting the form regularly …');
					$form.get(0).submit();
				}
			});

			e.preventDefault();
		});
	};

	/**
	 * Sets state classes (pristine/dirty, empty/filled) to input fields.
	 * @memberof module:Forms
	 */
	var initInputStates = function () {

		// initial state
		$('.' + options.classNames.form + ' ' + options.inputSelector).each(function () {
			var $field = $(this);

			if (_getInputValue($field)) {
				$field.addClass(options.classNames.dirty);
				$field.addClass(options.classNames.filled);
			}
			else {
				$field.addClass(options.classNames.pristine);
				$field.addClass(options.classNames.empty);
			}
		});

		// change events
		$(document).on('input change', '.' + options.classNames.form + ' ' + options.inputSelector, function () {
			var $field = $(this);

			if (_getInputValue($field)) {
				$field.removeClass(options.classNames.empty);
				$field.addClass(options.classNames.filled);
			}
			else {
				$field.addClass(options.classNames.empty);
				$field.removeClass(options.classNames.filled);
			}

			$field.removeClass(options.classNames.pristine);
			$field.addClass(options.classNames.dirty);
		});
	};

	/**
	 * Adds the ability to show/hide fields or make them required based on other fields' values. The behavior is set using a JSON config on the form element.
	 * @memberof module:Forms
	 *
	 * Example config:
	 * [
	 *     {
	 *         name: 'frm_salutation',						// name of the field to watch
	 *         value: 'company',							// value that triggers the condition
	 *         target: '[data-en-form-conditional="vat"]',	// target element
	 *         show_hide: true,								// enable show/hide behavior?
	 *         required: true								// enable required behavior?
	 *     }
	 * ]
	 */
	var initConditionalDisplay = function () {
		var that = this;

		$(document).on('change', '[' + options.conditionalAttribute + ']', function (e) {
			var $form = $(this);
			var $input = $(e.target);
			var rules = JSON.parse($form.attr(options.conditionalAttribute));

			_.each(rules, function (rule) {
				if ($input.attr('name') === rule.name) {
					var $target = $form.find(rule.target);

					if (_getInputValue($input) === rule.value) {
						if (rule.show_hide) {
							$target.removeClass(options.classNames.hidden);
						}

						if (rule.required) {
							$target.find(options.inputSelector).attr('required', true);
							$target.find(options.labelSelector).addClass(options.classNames.required);
						}
					}
					else {
						if (rule.show_hide) {
							$target.addClass(options.classNames.hidden);
						}

						if (rule.required) {
							$target.find(options.inputSelector).attr('required', false);
							$target.find(options.labelSelector).removeClass(options.classNames.required);
						}
					}
				}
			});
		});

		// check for inputs that are already active
		$(function () {
			$('[' + options.conditionalAttribute + ']').each(function () {
				var $form = $(this);
				var $inputs = $form.find(options.inputSelector);
				var rules = JSON.parse($form.attr(options.conditionalAttribute));

				_.each(rules, function (rule) {
					$inputs.filter('[name="' + rule.name + '"]').trigger('change');
				});
			});
		});
	};

	/**
	 * Sets custom options.
	 * @param {object} [settings] - Overrides defaults, see below.
	 * @property {object} classNames - Collection of class names that are set by the module.
	 * @memberof module:Forms
	 */
	var init = function (settings) {
		options = $.extend(true, {}, options, settings);
	};

	// exports
	return {
		init: init,
		initAjax: initAjax,
		initInputStates: initInputStates,
		initConditionalDisplay: initConditionalDisplay,
		setFieldClasses: setFieldClasses
	};
}(lodash, jQuery));
